import { PlusOutlined } from '@ant-design/icons';
import {
  InventoryPurchaseConfig,
  InventoryUOMMapping,
  Product,
  ProductInventory,
  ProductInventoryUnit,
  ProductStateCode,
  PurchaseBundleType,
  productPackings,
  productStates,
  productStorages,
} from '@gooduncles/gu-app-schema';
import {
  Button,
  Card,
  Col,
  Form,
  FormInstance,
  Input,
  InputNumber,
  Modal,
  Radio,
  Row,
  Select,
  Upload,
  message,
} from 'antd';
import type { UploadProps } from 'antd/es/upload/interface';
import { FC, useEffect, useState } from 'react';
import { productCategories, purchaseBundleToKr } from 'src/constants';

import { handleFormValidationError } from 'src/utils/antd/form-util';
import { formatDate } from 'src/utils/date-util';
import { callCreateProductWithInventory } from 'src/utils/firebase/firebase-callable';
import { getNewProductId, getProductInventoryId, uploadFile } from 'src/utils/firebase/firebase-short-cut';
import { getInventoryNameForProduct, inventoryPurchaseConfigTooltipMap } from 'src/utils/product-inventory-util';
import { calcProductUnitPrice, getProductFullName, parseProductMeasure } from 'src/utils/product-util';

import ProductSupplierSelector from 'src/atomic-components/atoms/Seletor/ProductSupplierSelector';

// 폼 값의 타입 정의
interface CreateProductFormValues {
  // 기본 정보
  name: string;
  brand: string | null;
  origin: string | null;
  price: number;
  taxFree: boolean;
  categories: string;
  storage: string;
  packing: string;
  state: ProductStateCode;
  measure: string;
  features: string[] | null;
  tags: string[] | null;
  mainTag: string | null;
  badge: string[] | null;
  imageDescription: string | null;
  dailyNotification: boolean;
  hidden: boolean;
  memo: string | null;
  packingInfo: string | null;
  image?: any;

  // 재고 정보
  productSupplierId: string;
  inventoryName: string;
  quantity: number;
  cost: number;
  inventoryUnit: ProductInventoryUnit;
  ratio: number;
  barcode?: string;
  purchaseProductName: string;
  purchaseBundle: PurchaseBundleType;
  purchaseMaxBundle?: number;
  purchaseMultiplyUnit: number;
  purchaseUnit: string;
}

const { Option } = Select;

/**
 * Product 필드 값을 정제하는 함수
 * - 빈 배열은 null로 변환
 * - 빈 문자열은 null로 변환
 * - undefined는 null로 변환
 */
const sanitizeProductField = <T,>(value: T | null | undefined): T | null => {
  // undefined인 경우 null 반환
  if (value === undefined) return null;

  // null인 경우 그대로 반환
  if (value === null) return null;

  // 배열인 경우 빈 배열이면 null 반환
  if (Array.isArray(value) && value.length === 0) return null;

  // 문자열인 경우 빈 문자열이면 null 반환
  if (typeof value === 'string' && value.trim() === '') return null;

  return value;
};

const getProductFullNamePreview = (form: FormInstance) => {
  const values = form.getFieldsValue();
  const measure = parseProductMeasure(values.measure);
  const fullName = getProductFullName({ ...values, measure });
  return fullName;
};

// 폼 초기값 설정
const initialValues: Partial<CreateProductFormValues> = {
  taxFree: false,
  state: ProductStateCode.STOCK,
  dailyNotification: false,
  hidden: false,
  inventoryUnit: ProductInventoryUnit.EA,
  purchaseBundle: PurchaseBundleType.ALL,
  purchaseMultiplyUnit: 1,
  purchaseUnit: '개',
  ratio: 1,
  quantity: 0,
  categories: 'unknown',
  storage: 'freezer',
  packing: 'others',
  features: undefined,
  tags: undefined,
  mainTag: undefined,
  badge: undefined,
  imageDescription: undefined,
  memo: undefined,
  packingInfo: undefined,
};

const parseToProductInventory = (values: {
  productSupplierId: string;
  inventoryName: string;
  quantity: number;
  cost: number;
  inventoryUnit: ProductInventoryUnit;
  ratio: number;
  barcode: string;
  purchaseProductName: string;
  purchaseBundle: PurchaseBundleType;
  purchaseMaxBundle: number;
  purchaseMultiplyUnit: number;
  purchaseUnit: string;
}) => {
  const inventoryUOMMapping: InventoryUOMMapping = {
    [values.inventoryUnit]: {
      ratio: values.ratio,
      cost: values.cost,
      barcode: values.barcode,
    },
  };
  const purchaseConfig: InventoryPurchaseConfig = {
    supplierId: values.productSupplierId,
    purchaseProductName: values.purchaseProductName,
    purchaseBundle: values.purchaseBundle,
    purchaseMaxBundle: values.purchaseMaxBundle,
    purchaseMultiplyUnit: values.purchaseMultiplyUnit,
    purchaseUnit: values.purchaseUnit,
  };
  const productInventory: ProductInventory & { _id: string } = {
    _id: getProductInventoryId(),
    inventoryName: values.inventoryName,
    quantity: values.quantity,
    uom: inventoryUOMMapping,
    purchaseConfig,
    deletedAt: null,
    active: true,
    optimalStockMemo: null,
  };
  return productInventory;
};

const parseFormValuesToProduct = async (values: CreateProductFormValues) => {
  // 재고와 관련된 필드는 별도로 처리한다.
  const {
    productSupplierId,
    inventoryName,
    quantity,
    cost,
    inventoryUnit,
    ratio,
    barcode,
    purchaseProductName,
    purchaseBundle,
    purchaseMaxBundle,
    purchaseMultiplyUnit,
    purchaseUnit,
  } = values;
  const productInventory = parseToProductInventory({
    productSupplierId,
    inventoryName,
    quantity: quantity === Infinity ? Number.MAX_SAFE_INTEGER : quantity,
    cost,
    inventoryUnit,
    ratio,
    barcode: barcode || '',
    purchaseProductName,
    purchaseBundle,
    purchaseMaxBundle: purchaseMaxBundle || 0,
    purchaseMultiplyUnit,
    purchaseUnit,
  });
  const productId = await getNewProductId();
  // measure 문자열을 ProductMeasure[] 타입으로 파싱
  const measure = parseProductMeasure(values.measure) ?? [{ amount: 1, unit: '개' }];

  // categories를 배열로 변환 - categories가 단일 값인 경우
  const categories = values.categories ? [values.categories, 'all'] : ['unknown', 'all'];

  // fullName 계산
  const fullName = getProductFullName({ ...values, measure });

  // unitPrice 계산
  const unitPrice = measure && values.price ? calcProductUnitPrice(measure, values.price) : null;

  // 상품 정보 구성
  const product: Product = {
    productId,
    inventoryId: productInventory._id,
    inventoryUnit,
    measure,
    categories,
    fullName,
    unitPrice,
    cost,
    supplierId: productSupplierId,
    state: values.state,
    taxFree: values.taxFree,
    price: values.price,
    costUpdatedAt: formatDate(new Date(), "yyyy-MM-dd'T'HH:mm:ss+0900"),
    hidden: values.hidden,
    brand: sanitizeProductField(values.brand),
    packing: values.packing,
    storage: values.storage,
    origin: sanitizeProductField(values.origin),
    features: sanitizeProductField(values.features),
    mainTag: sanitizeProductField(values.mainTag),
    tags: sanitizeProductField(values.tags),
    packingInfo: sanitizeProductField(values.packingInfo),
    badge: sanitizeProductField(values.badge),
    dailyNotification: values.dailyNotification,
    imageDescription: sanitizeProductField(values.imageDescription),
    salesAmount: 0,
    name: values.name,
    imageUrl: null,
    thumbnail: null,
    memo: sanitizeProductField(values.memo),
    createSnapshot: true,
  };

  return {
    product,
    productInventory,
  };
};

const CreateNewProductModal: FC = () => {
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [form] = Form.useForm<CreateProductFormValues>();
  const [preview, setPreview] = useState<string>('');

  // 미리보기 업데이트 함수
  const updatePreview = () => {
    const fullNamePreview = getProductFullNamePreview(form);
    setPreview(fullNamePreview);
  };

  const handleCancel = () => {
    setOpen(false);
    form.resetFields();
  };

  const handleOk = async () => {
    try {
      setLoading(true);
      const values = await form.validateFields();
      const { product, productInventory } = await parseFormValuesToProduct(values);
      const response = await callCreateProductWithInventory({
        body: {
          product,
          inventory: productInventory,
        },
      });
      if (!response.data.result || response.data.result === 'error') {
        throw new Error(`상품 & 재고 추가 실패 - ${response.data.reason ?? '알 수 없는 오류'}`);
      }
      message.success('상품 & 재고 추가 성공');

      // 이미지 경로는 반드시 서버가 확정한 ID를 쓴다. 클라이언트가 채번한 product.productId 는
      // 서버의 idGeneratorProduct 채번과 어긋날 수 있어, 그대로 쓰면 엉뚱한 상품에 이미지가 붙는다.
      const createdProductId = response.data.productId ?? product.productId;
      // 이미지 업로드 부분 수정
      const fileList = form.getFieldValue('image');
      if (fileList?.[0]?.originFileObj) {
        const file = fileList[0].originFileObj;
        await uploadFile(`product-image/${createdProductId}`, file, {
          contentType: file.type || 'image/jpeg',
          cacheControl: 'public,max-age=864000',
        });
      } else {
        message.error('업로드할 이미지가 없습니다');
      }

      setOpen(false);
      form.resetFields();
    } catch (error) {
      console.error(error);

      // Form 검증 오류 처리 (유틸리티 함수 사용)
      handleFormValidationError(error, '신규 상품 추가 실패');
    } finally {
      setLoading(false);
    }
  };

  const normFile = (e: any) => {
    if (Array.isArray(e)) {
      return e;
    }
    return e?.fileList;
  };

  const beforeUpload: UploadProps['beforeUpload'] = (file) => {
    const isImage = file.type.startsWith('image/');
    if (!isImage) {
      message.error('이미지 파일만 업로드 가능합니다!');
      return Upload.LIST_IGNORE;
    }
    return false; // 자동 업로드 방지
  };

  useEffect(() => {
    if (open) {
      callCreateProductWithInventory({ body: null }).then((res) => {
        console.log(`[preflight] callCreateProductWithInventory: ${res.data.result}`, res);
      });
    }
  }, [open]);

  return (
    <>
      <Button type='primary' icon={<PlusOutlined />} onClick={() => setOpen(true)}>
        신규 상품 추가
      </Button>

      <Modal
        title='신규 상품 추가'
        open={open}
        onCancel={handleCancel}
        onOk={handleOk}
        confirmLoading={loading}
        width={1000}
        style={{ top: 20 }}
        styles={{
          body: {
            maxHeight: '80vh',
            overflow: 'auto',
            paddingRight: 10,
          },
        }}
      >
        <Form form={form} layout='vertical' onValuesChange={updatePreview} initialValues={initialValues}>
          <Row gutter={16}>
            <Col span={12}>
              {/* 왼쪽 컬럼 - 기본 정보 */}
              <Form.Item
                name='name'
                label='상품명'
                rules={[{ required: true, message: '상품명을 입력해주세요' }]}
                extra={preview}
              >
                <Input placeholder='상품명을 입력하세요' />
              </Form.Item>

              <Form.Item name='brand' label='브랜드'>
                <Input placeholder='브랜드를 입력하세요' />
              </Form.Item>

              <Form.Item name='origin' label='원산지'>
                <Input placeholder='원산지를 입력하세요' />
              </Form.Item>

              <Form.Item name='price' label='판매가' rules={[{ required: true, message: '판매가를 입력해주세요' }]}>
                <InputNumber
                  style={{ width: '100%' }}
                  min={0}
                  formatter={(value) => `${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ',')}
                />
              </Form.Item>

              <Form.Item name='taxFree' label='면세여부'>
                <Radio.Group>
                  <Radio value={true}>면세</Radio>
                  <Radio value={false}>과세</Radio>
                </Radio.Group>
              </Form.Item>

              <Form.Item
                name='categories'
                label='카테고리'
                rules={[{ required: true, message: '카테고리를 선택해주세요' }]}
              >
                <Select placeholder='카테고리를 선택하세요'>
                  {Object.entries(productCategories).map(([key, value]) => (
                    <Option key={key} value={key}>
                      {value}
                    </Option>
                  ))}
                </Select>
              </Form.Item>

              <Form.Item
                name='storage'
                label='보관방식'
                rules={[{ required: true, message: '보관방식을 선택해주세요' }]}
              >
                <Select placeholder='보관방식을 선택하세요'>
                  {Object.entries(productStorages).map(([key, value]) => (
                    <Option key={key} value={key}>
                      {value}
                    </Option>
                  ))}
                </Select>
              </Form.Item>

              <Form.Item
                name='packing'
                label='포장단위'
                rules={[{ required: true, message: '포장단위를 선택해주세요' }]}
              >
                <Select placeholder='포장단위를 선택하세요'>
                  {Object.entries(productPackings).map(([key, value]) => (
                    <Option key={key} value={key}>
                      {value}
                    </Option>
                  ))}
                </Select>
              </Form.Item>

              <Form.Item name='state' label='판매상태' rules={[{ required: true, message: '판매상태를 선택해주세요' }]}>
                <Select placeholder='판매상태를 선택하세요'>
                  {Object.entries(productStates).map(([key, value]) => (
                    <Option key={key} value={key}>
                      {value}
                    </Option>
                  ))}
                </Select>
              </Form.Item>

              <Form.Item name='measure' label='판매단위' rules={[{ required: true, message: '단위를 입력해주세요' }]}>
                <Input placeholder='판매단위를 입력하세요 ex) 1kg*10개' />
              </Form.Item>

              <Form.Item name='image' label='상품 이미지' valuePropName='fileList' getValueFromEvent={normFile}>
                <Upload listType='picture-card' beforeUpload={beforeUpload} maxCount={1} accept='image/*'>
                  <button style={{ color: 'inherit', cursor: 'inherit', border: 0, background: 'none' }} type='button'>
                    <PlusOutlined />
                    <div style={{ marginTop: 8 }}>이미지 업로드</div>
                  </button>
                </Upload>
              </Form.Item>

              <Form.Item name='features' label='특징'>
                <Select mode='tags' placeholder='특징을 입력하세요' />
              </Form.Item>

              <Form.Item name='tags' label='태그'>
                <Select mode='tags' placeholder='태그를 입력하세요' />
              </Form.Item>

              <Form.Item name='mainTag' label='대표태그'>
                <Input placeholder='대표태그를 입력하세요' />
              </Form.Item>

              <Form.Item name='badge' label='배지'>
                <Select mode='tags' placeholder='배지를 입력하세요' />
              </Form.Item>

              <Form.Item name='imageDescription' label='이미지 설명'>
                <Input placeholder='이미지 설명을 입력하세요' />
              </Form.Item>

              <Form.Item name='dailyNotification' label='일일 가격 알림'>
                <Radio.Group>
                  <Radio value={true}>활성화</Radio>
                  <Radio value={false}>비활성화</Radio>
                </Radio.Group>
              </Form.Item>

              <Form.Item name='hidden' label='숨김상품'>
                <Radio.Group>
                  <Radio value={true}>숨김</Radio>
                  <Radio value={false}>표시</Radio>
                </Radio.Group>
              </Form.Item>

              <Form.Item name='memo' label='비고사항'>
                <Input.TextArea placeholder='비고사항을 입력하세요' />
              </Form.Item>

              <Form.Item name='packingInfo' label='포장시 주의사항'>
                <Input.TextArea placeholder='포장시 주의사항을 입력하세요' />
              </Form.Item>
            </Col>

            <Col span={12}>
              {/* 오른쪽 컬럼 - 재고정보 */}
              <Form.Item
                name='productSupplierId'
                label='거래처'
                rules={[{ required: true, message: '거래처를 선택해주세요' }]}
              >
                <ProductSupplierSelector
                  onProductSupplierChange={(productSupplierId) => {
                    form.setFieldsValue({ productSupplierId });
                  }}
                />
              </Form.Item>

              <Form.Item
                name='inventoryName'
                label='재고명'
                rules={[{ required: true, message: '재고명을 입력해주세요' }]}
              >
                <Input
                  placeholder='재고명을 입력하세요'
                  addonAfter={
                    <Button
                      size='small'
                      type='text'
                      onClick={() => {
                        const inventoryName = getInventoryNameForProduct(form.getFieldsValue());
                        form.setFieldsValue({ inventoryName });
                      }}
                    >
                      자동생성
                    </Button>
                  }
                />
              </Form.Item>

              <Form.Item name='quantity' label='재고 수량'>
                <InputNumber
                  style={{ width: '100%' }}
                  min={0}
                  addonAfter={
                    <Button
                      size='small'
                      type='text'
                      onClick={() => form.setFieldsValue({ quantity: Infinity })}
                      style={{ padding: '0 8px' }}
                    >
                      무제한
                    </Button>
                  }
                />
              </Form.Item>

              <Form.Item name='cost' label='매입가' rules={[{ required: true, message: '매입가를 입력해주세요' }]}>
                <InputNumber style={{ width: '100%' }} min={0} />
              </Form.Item>

              <Form.Item
                name='inventoryUnit'
                label='재고 단위'
                rules={[{ required: true, message: '재고 단위를 선택해주세요' }]}
              >
                <Radio.Group>
                  <Radio value={ProductInventoryUnit.EA}>ea</Radio>
                  <Radio value={ProductInventoryUnit.BUNDLE}>bundle</Radio>
                </Radio.Group>
              </Form.Item>

              <Form.Item
                name='ratio'
                label='재고 단위 비율'
                rules={[{ required: true, message: '재고 단위 비율을 입력해주세요' }]}
              >
                <InputNumber style={{ width: '100%' }} min={1} />
              </Form.Item>

              <Form.Item name='barcode' label='바코드'>
                <Input placeholder='바코드를 입력하세요' />
              </Form.Item>

              <Card>
                <Form.Item
                  name='purchaseProductName'
                  label='발주 품명(미입력시 재고명으로 발주)'
                  tooltip={{
                    title: inventoryPurchaseConfigTooltipMap.purchaseProductName,
                    overlayStyle: { whiteSpace: 'pre-line' },
                  }}
                >
                  <Input placeholder='발주 품명을 입력하세요' />
                </Form.Item>

                <Form.Item
                  name='purchaseBundle'
                  label='분할 단위'
                  tooltip={{
                    title: inventoryPurchaseConfigTooltipMap.purchaseBundle,
                    overlayStyle: { whiteSpace: 'pre-line' },
                  }}
                  rules={[{ required: true, message: '분할 단위를 선택해주세요' }]}
                >
                  <Select>
                    {Object.values(PurchaseBundleType).map((type) => (
                      <Select.Option key={type} value={type}>
                        {purchaseBundleToKr[type as keyof typeof purchaseBundleToKr]}
                      </Select.Option>
                    ))}
                  </Select>
                </Form.Item>

                <Form.Item
                  name='purchaseMaxBundle'
                  label='발주 가능 최대 묶음(0은 없음으로 처리)'
                  tooltip={{
                    title: inventoryPurchaseConfigTooltipMap.purchaseMaxBundle,
                    overlayStyle: { whiteSpace: 'pre-line' },
                  }}
                >
                  <InputNumber style={{ width: '100%' }} min={0} />
                </Form.Item>

                <Form.Item
                  name='purchaseMultiplyUnit'
                  label='발주 단위 변환 값'
                  tooltip={{
                    title: inventoryPurchaseConfigTooltipMap.purchaseMultiplyUnit,
                    overlayStyle: { whiteSpace: 'pre-line' },
                  }}
                  rules={[{ required: true, message: '발주 단위 변환 값을 입력해주세요' }]}
                >
                  <InputNumber style={{ width: '100%' }} min={0} />
                </Form.Item>

                <Form.Item
                  name='purchaseUnit'
                  label='발주 단위(빈 값은 "개"로 처리)'
                  tooltip={{
                    title: inventoryPurchaseConfigTooltipMap.purchaseUnit,
                    overlayStyle: { whiteSpace: 'pre-line' },
                  }}
                >
                  <Input placeholder='발주 단위를 입력하세요' />
                </Form.Item>
              </Card>
            </Col>
          </Row>
        </Form>
      </Modal>
    </>
  );
};

export default CreateNewProductModal;
